/* eslint-disable no-param-reassign */
import gql from 'graphql-tag';
import { flatten, get, mapValues, pick, omitBy, isNil } from 'lodash';
import { ID_PREFIX, PUBLISHED_STATUS, DISCLAIMER_LOCATION_TYPES, HOST_SET_ROUTE, GDPR_SETTING } from 'utils/constants';
import { pseudoTagStringFromGQL, pseudoTagStringToGQL, taxiFieldNameToStandardFieldName } from 'utils/coercionUtils';
import Graph from 'utils/conditionality.graph';
import { filterAnswersByActive } from 'utils/branchingUtils';
import Coercion from './gql.coercion';
import getClient from './build.gql.client';

export const client = getClient();

const formFragment = gql`
  fragment formFragment on Form {
    id
    gdprSetting
    formStyle
    title
    introText
    introSubText
    thankYouURL
    leadSource
    splashCreative
    name
    program
    programOfStudy {
      id
      publicAlias
      credential_type_id
    }
    isPublished
    lastPublished
    publishedStatus
    extras {
      branchingFields
      syntheticContentEntities {
        disclaimers {
          BODY {
            show
            value
            override
          }
          FOOTER {
            show
            value
            override
          }
        }
        inferredFields
      }
    }
    formMetadata {
      isFormJSONEditable
      areAllFieldsOptional
      experimentalTrialAttributes
      formAttributesConfig
    }
    screens {
      id
      isSubmittable
      isStartScreen
      edges {
        id
        target
        condition
      }
      fields {
        id
        hidden
        label
        name
        required
        type
        value
        flags
        uiOrder
        condition
        helpText
        thankYouPageURLs
        overrides {
          id
          label
          helpText
          required
          hidden
          value
          useCase
        }
      }
    }
    grouping {
      name
      id
    }
  }
`;

const makeFieldsGraphQLObject = (fields, fieldOrder, offset, thankYouRoutingFields, gdprSetting) => {
  let actualIncludes = [...fieldOrder];
  if (offset === 0) {
    actualIncludes = actualIncludes.concat(
      Object.values(fields)
        .filter((field) => field.delete && !field.new)
        .map((field) => field.id)
    );
  }

  return actualIncludes.map((fieldId) => {
    const field = fields[fieldId];
    const coercion = new Coercion(field);
    const coercedFieldData = coercion.exists() ? coercion.toGQL(gdprSetting) : { value: field.value };
    let finalThankYouPageUrls = null;
    if (thankYouRoutingFields[fieldId]) {
      finalThankYouPageUrls = {};
      Object.keys(thankYouRoutingFields[fieldId]).forEach((key) => {
        finalThankYouPageUrls[key] = thankYouRoutingFields[fieldId][key] === HOST_SET_ROUTE ? '' : thankYouRoutingFields[fieldId][key];
      });
    }

    let overridesArray = null;
    if (field.overrides && typeof field.overrides === 'object') {
      overridesArray = Object.keys(field.overrides).map((useCase) => {
        const override = field.overrides[useCase];
        const coercedOverrideData = (coercion.exists() && coercion.overrideExists()) ?
          coercion.overrideToGQL(override, gdprSetting) : { value: override.value };
        const gqlOverride = {
          useCase,
          label: isNil(override.label) ? null : pseudoTagStringToGQL(override.label),
          helpText: isNil(override.helpText?.value) ? null : pseudoTagStringToGQL(override.helpText.value),
          required: override.required ?? null,
          hidden: override.hidden ?? null,
          value: override.editors || null,
          ...coercedOverrideData,
        };

        if (override.id) {
          gqlOverride.id = +override.id;
        }

        if (override.delete) {
          gqlOverride.delete = true;
        }

        return gqlOverride;
      });
    }

    const gqlField = {
      hidden: field.hidden,
      label: pseudoTagStringToGQL(field.label),
      name: field.name,
      required: field.required,
      type: field.type,
      flags: field.flags,
      uiOrder: fieldOrder.indexOf(field.id) + offset,
      condition: field.condition,
      helpText: field.helpText.hasHelpText ? pseudoTagStringToGQL(field.helpText.value) : '',
      thankYouPageURLs: finalThankYouPageUrls,
      overrides: overridesArray,
      ...coercedFieldData, // Must come last, so it can overwrite other data (needed for name parameter on consent fields).
    };

    if (!field.new) {
      gqlField.id = +field.id;
    }
    if (field.delete) {
      gqlField.delete = true;
    }

    return gqlField;
  });
};

const makeConditionalityInfoFromResponse = (branchingFieldUiOrder, fieldOrder) => Object.keys(branchingFieldUiOrder).reduce((memo, uiOrder) => {
  const fieldId = fieldOrder[uiOrder];
  memo[fieldId] = [...branchingFieldUiOrder[uiOrder]]; // eslint-disable-line no-param-reassign
  return memo;
}, {});

export function convertToExtras({ fieldConditionalityInfo, fieldOrder, syntheticContentEntities }) {
  const gdprSetting = (syntheticContentEntities?.gdprSetting || GDPR_SETTING.NEVER).split('-')[0];
  return {
    extras: {
      branchingFields: Object.keys(fieldConditionalityInfo.branchingFields).reduce((memo, f) => ({ ...memo, [fieldOrder.indexOf(f)]: fieldConditionalityInfo.branchingFields[f] }), {}),
      syntheticContentEntities: {
        disclaimers: mapValues(get(syntheticContentEntities, 'disclaimers', {}), ({ value, show, override }) => ({ show, value: show ? pseudoTagStringToGQL(value) : '', override: show && override ? pseudoTagStringToGQL(override) : '' })),
        inferredFields: get(syntheticContentEntities, 'inferredFields', {}),
      },
    },
    gdprSetting,
  };
}

export function convertThankYouUrls(fields) {
  const fieldIds = Object.keys(fields);

  const buildRoutingFields = (routingFieldsObj, fieldId) => {
    if (fields[fieldId].thankYouPageURLs) {
      routingFieldsObj[fieldId] = fields[fieldId].thankYouPageURLs; // eslint-disable-line no-param-reassign
      Object.keys(routingFieldsObj[fieldId]).forEach((key) => {
        if (!routingFieldsObj[fieldId][key]) {
          // eslint-disable-next-line no-param-reassign
          routingFieldsObj[fieldId][key] = HOST_SET_ROUTE;
        }
      });
    }
    return routingFieldsObj;
  };

  return { thankYouRoutingFields: fieldIds.reduce(buildRoutingFields, {}) };
}

export function convertFromExtras(extras, fieldOrder, formAttributesConfig, topLevelGDPR) {
  let gdprSetting = topLevelGDPR || GDPR_SETTING.NEVER;
  if ([GDPR_SETTING.ALWAYS, GDPR_SETTING.GEO].includes(gdprSetting) && formAttributesConfig?.impliedConsent) {
    gdprSetting = `${gdprSetting}-implied`;
  }

  return {
    fieldConditionalityInfo: {
      branchingFields: makeConditionalityInfoFromResponse(extras.branchingFields, fieldOrder),
    },
    syntheticContentEntities: {
      gdprSetting,
      disclaimers: mapValues(pick(get(extras, 'syntheticContentEntities.disclaimers'), Object.values(DISCLAIMER_LOCATION_TYPES)), ({ value, show, override }) => ({ show, value: show ? pseudoTagStringFromGQL(value) : '', override: show && override ? pseudoTagStringFromGQL(override) : '' })),
      inferredFields: get(extras, 'syntheticContentEntities.inferredFields') || {},
    },
  };
}

const makeFormStateObjectFromResponse = (res) => {
  const response = { ...res };
  response.screens = [...response.screens];
  response.screens.sort((a, b) => a.fields[0].uiOrder - b.fields[0].uiOrder);
  const retval = {
    id: response.id,
    formStyle: response.formStyle,
    title: response.title,
    introText: response.introText,
    introSubText: response.introSubText,
    thankYouURL: response.thankYouURL,
    leadSource: response.leadSource,
    splashCreative: response.splashCreative,
    name: response.name,
    groupingId: response.grouping.id,
    groupingName: response.grouping.name,
    isPublished: response.isPublished,
    lastPublished: response.lastPublished,
    publishedStatus: response.publishedStatus,
    degreeOffering: response.program.degreeOffering,
    programFullName: response.program.fullName,
    programId: response.program.id,
    programOffering: response.program.offering,
    programOfStudy: response.programOfStudy,
    salesforceOrg: response.program.salesforceOrg,
    formMetadata: response.formMetadata,
    isExperimentalForm: response.isExperimentalForm,
  };
  retval.screens = response.screens.map((screen) => ({
    id: screen.id,
    out: screen.edges.reduce((memo, edge) => {
      memo[edge.target] = { // eslint-disable-line no-param-reassign
        id: edge.id,
        condition: edge.condition,
      };
      return memo;
    }, {}),
  }));
  const allFields = flatten(response.screens.map((s) => s.fields));
  retval.fieldOrder = response.screens.reduce((memo, screen) => [...memo, `screen-${screen.id}`, ...screen.fields.map((f) => f.id)], []);
  retval.formMetadata = {
    isFormJSONEditable: retval.formMetadata.isFormJSONEditable,
    areAllFieldsOptional: retval.formMetadata.areAllFieldsOptional,
    experimentalTrialAttributes: retval.formMetadata.experimentalTrialAttributes,
    formAttributesConfig: retval.formMetadata.formAttributesConfig,
  };
  const extras = convertFromExtras(response.extras, [...retval.fieldOrder], retval.formMetadata.formAttributesConfig, response.gdprSetting);

  retval.fields = allFields.reduce((memo, field) => {
    let unpackedField = { ...field };
    unpackedField.helpText = {
      hasHelpText: Boolean(unpackedField.helpText),
      value: unpackedField.helpText ? pseudoTagStringFromGQL(unpackedField.helpText) : '',
    };
    unpackedField.label = pseudoTagStringFromGQL(unpackedField.label);

    const fieldID = field.id;
    const coercion = new Coercion(field);
    const { gdprSetting } = extras.syntheticContentEntities;
    if (coercion.exists()) {
      unpackedField = coercion.fromGQL(unpackedField, gdprSetting);
    }

    if (unpackedField.overrides && Array.isArray(unpackedField.overrides)) {
      unpackedField.overrides = unpackedField.overrides.reduce((overridesObj, override) => {
        let { useCase, ...unpackedOverride } = { ...override }; // eslint-disable-line prefer-const
        if (!isNil(unpackedOverride.helpText)) {
          unpackedOverride.helpText = { value: pseudoTagStringFromGQL(unpackedOverride.helpText) };
        }
        if (coercion.exists() && coercion.overrideExists()) {
          unpackedOverride = coercion.overrideFromGQL(unpackedOverride, gdprSetting);
        }
        overridesObj[useCase] = omitBy({
          id: unpackedOverride.id,
          label: unpackedOverride.label || '',
          helpText: {
            value: unpackedOverride.helpText || '',
          },
          required: unpackedOverride.required ?? null,
          hidden: unpackedOverride.hidden ?? null,
          editors: unpackedOverride.value || null,
          ...unpackedOverride,
        }, isNil);
        return overridesObj;
      }, {});
    } else {
      unpackedField.overrides = {};
    }

    memo[fieldID] = unpackedField; // eslint-disable-line no-param-reassign
    return memo;
  }, {});

  return { ...retval, ...convertThankYouUrls(retval.fields), ...extras };
};

const makeScreensGraphQLObject = (screens, fields, fieldOrder, thankYouRoutingFields, gdprSetting) => {
  const liveScreens = screens.filter((screen) => !screen.delete);
  const screenIds = liveScreens.map((s) => `screen-${s.id}`);
  const indicies = fieldOrder.filter((id) => screenIds.includes(id)).map((id) => fieldOrder.indexOf(id));
  const finalScreens = [];

  indicies.forEach((i, index) => {
    const containedFields = fieldOrder.slice(i + 1, indicies[index + 1]);
    const gqlScreen = {
      isSubmittable: index === indicies.length - 1,
      isStartScreen: i === 0,
      fields: makeFieldsGraphQLObject(fields, containedFields, i, thankYouRoutingFields, gdprSetting),
    };

    if (liveScreens[index].id && String(liveScreens[index].id).indexOf(ID_PREFIX) === -1) {
      gqlScreen.id = liveScreens[index].id;
    }
    gqlScreen.edges = Object.keys(liveScreens[index].out || {}).map((outScreen) => {
      const outMap = {
        target: index + 1,
        condition: liveScreens[index].out[outScreen].condition,
      };

      if (liveScreens[index].out[outScreen].id) {
        outMap.id = liveScreens[index].out[outScreen].id;
      }
      return outMap;
    });
    finalScreens.push(gqlScreen);
  });

  screens.filter((s) => s.delete && String(s.id).indexOf(ID_PREFIX) === -1).forEach((s) => {
    finalScreens.push({
      id: s.id,
      delete: true,
      isSubmittable: false,
      isStartScreen: false,
      fields: [],
      edges: [],
    });
  });

  return finalScreens;
};

const getActiveBranchingAnswers = (branchingFieldMap, fields) => {
  let activeBranchingAnswers = [];
  if (Object.keys(branchingFieldMap).length) {
    const branchingFieldId = Object.keys(branchingFieldMap)[0];
    activeBranchingAnswers = filterAnswersByActive(branchingFieldMap[branchingFieldId], fields[branchingFieldId]);
  }
  return activeBranchingAnswers;
};

export function addForm({ name, fieldConditionalityInfo, formStyle, title, introText, introSubText, thankYouURL, leadSource, splashCreative, programId, programOfStudy, fields, screens, fieldOrder, groupingId, publishedStatus, syntheticContentEntities, formMetadata }) {
  const g = new Graph(fieldConditionalityInfo.dehydratedGraph);
  const activeBranchingAnswers = getActiveBranchingAnswers(fieldConditionalityInfo.branchingFields, fields);
  const condStrings = g.exportConditionStrings(activeBranchingAnswers, Object.keys(fields).reduce((memo, key) => {
    memo[fields[key].id] = taxiFieldNameToStandardFieldName(fields[key].name); // eslint-disable-line no-param-reassign
    return memo;
  }, {}));
  Object.keys(condStrings).forEach((key) => { fields[key].condition = condStrings[key]; }); // eslint-disable-line no-param-reassign
  const posID = programOfStudy && programOfStudy.id ? programOfStudy.id : null;
  const screensGQLObject = makeScreensGraphQLObject(screens, fields, fieldOrder, {}, syntheticContentEntities.gdprSetting);
  const { extras, gdprSetting } = convertToExtras({ fieldConditionalityInfo, fieldOrder, syntheticContentEntities });
  const formPackage = {
    name,
    formMetadata,
    gdprSetting,
    extras,
    formStyle,
    title,
    introText,
    introSubText,
    thankYouURL,
    leadSource,
    splashCreative,
    programId,
    programOfStudy: posID,
    groupingId,
    publishedStatus,
    screens: screensGQLObject,
  };
  const mutation = gql`
  mutation addForm($formPackage: FormInput) {
    addForm(form: $formPackage) {
      ...formFragment
    }
  }
  ${formFragment}
  `;
  return client.mutate({ mutation, variables: { formPackage } }).then((response) => makeFormStateObjectFromResponse(response.data.addForm));
}

export function cloneForm(id, shouldMakeExperimental) {
  const mutation = gql`
    mutation {
      cloneForm(form: {id: ${id}}, shouldMakeExperimental: ${shouldMakeExperimental}) {
        id
      }
    }
  `;
  return client.mutate({ mutation }).then((response) => response.data);
}

export function getGroupings() {
  const queryParam = gql`query {
     groupings
      {
          id
          name
          programs
          programsOfStudy {
            id
            centralParkUUID
            publicAlias
            credential_type_id
          }
          universityName
        }
  }`;
  return client.query({ query: queryParam }).then((response) => response.data.groupings);
}
export function getCredentialTypes() {
  const queryParam = gql`query {
    credentialTypes{
      id
      displayName
      value
  }
 }`;
  return client.query({ query: queryParam }).then((response) => response.data.credentialTypes);
}

export function getCredentialType(credentialTypeId) {
  const queryParam = gql`query {
    credentialType(id: ${credentialTypeId}){
      id
      displayName
      value
      groupings {
        id
        name
        universityName
        programs
      }
    }
 }`;
  return client.query({ query: queryParam }).then((response) => response.data.credentialType);
}

export function getGrouping(groupingId, shouldIncludeForms = true) {
  const query = gql`query {
    grouping(id: ${groupingId}) {
      name
      id
      programs
      programsOfStudy {
        id
        centralParkUUID
        publicAlias
        credential_type_id
      }
      ${shouldIncludeForms ? ' forms {id, name, program, publishedStatus, isExperimentalForm, gdprSetting}' : ''}
    }
  }`;
  return client.query({ query, fetchPolicy: 'network-only' }).then((response) => response.data.grouping);
}

export function updateForm({ id, fieldConditionalityInfo, formStyle, title, introText, introSubText, thankYouURL, leadSource, splashCreative, name, programId, programOfStudy, screens, fields, fieldOrder, publishedStatus, syntheticContentEntities, thankYouRoutingFields, formMetadata }) {
  const g = new Graph(fieldConditionalityInfo.dehydratedGraph);
  const activeBranchingAnswers = getActiveBranchingAnswers(fieldConditionalityInfo.branchingFields, fields);
  const condStrings = g.exportConditionStrings(activeBranchingAnswers, Object.keys(fields).reduce((memo, key) => {
    memo[fields[key].id] = taxiFieldNameToStandardFieldName(fields[key].name); // eslint-disable-line no-param-reassign
    return memo;
  }, {}));

  Object.keys(condStrings).forEach((key) => { fields[key].condition = condStrings[key]; }); // eslint-disable-line no-param-reassign
  const posID = programOfStudy && programOfStudy.id ? programOfStudy.id : null;
  const screensGQLObject = makeScreensGraphQLObject(screens, fields, fieldOrder, thankYouRoutingFields, syntheticContentEntities.gdprSetting);
  const { extras, gdprSetting } = convertToExtras({ fieldConditionalityInfo, fieldOrder, syntheticContentEntities });
  const formPackage = {
    id,
    formMetadata,
    gdprSetting,
    extras,
    formStyle,
    title,
    introText,
    introSubText,
    thankYouURL,
    leadSource,
    splashCreative,
    name,
    programId,
    programOfStudy: posID,
    publishedStatus: publishedStatus === PUBLISHED_STATUS.UNPUBLISHED ? publishedStatus : PUBLISHED_STATUS.MODIFIED,
    screens: screensGQLObject,
  };
  const mutation = gql`
  mutation updateForm($formPackage: FormChangeInput) {
    updateForm(form: $formPackage) {
      ...formFragment
      isExperimentalForm
    }
  }
  ${formFragment}
  `;
  return client.mutate({ mutation, variables: { formPackage } }).then((response) => makeFormStateObjectFromResponse(response.data.updateForm));
}

export function getForm(id) {
  const query = gql`query{
  form(id: ${id}) {
    ...formFragment
    isExperimentalForm
  }
}
${formFragment}
`;
  return client.query({ query }).then((response) => makeFormStateObjectFromResponse(response.data.form));
}

export function deleteForm(id) {
  const mutation = gql`
    mutation {
      deleteForm(form: {id: ${id}} ) {
        deletedCount
      }
    }
  `;
  return client.mutate({ mutation }).then(((response) => response.data));
}

export function getGeneralSettings() {
  const query = gql`
    query {
      generalSettings {
        pluginVersion
      }
    }
  `;
  return client.query({ query }).then((response) => response.data);
}

export function setGeneralSettings(settingsObj) {
  const mutation = gql`
  mutation updateGeneralSettings($Settings: GeneralSettingsChangeInput) {
    updateGeneralSettings(settings: $Settings){
      pluginVersion
    }
    }
  `;
  return client.mutate({ mutation, variables: { Settings: settingsObj } }).then((response) => response.data);
}
